/**
 * useDefaultStatusFilter — Generic hook for applying a default status filter
 * on the main table at the start of each browser session.
 *
 * Session behavior:
 * - Default applied once per (tenant × session) via sessionStorage gate.
 * - Silent auto-clear fires only when the table is empty AND filter state
 *   content-equals what we wrote for the CURRENT sessionKey (user hasn't
 *   touched it). Applied filters are tracked per sessionKey, so a single
 *   hook instance cycling through multiple sessionKeys (e.g. Seeds tabs)
 *   keeps each key's provenance independent.
 * - `lastAppliedSessionKey` allows mid-session tenant switches (new sessionKey)
 *   to re-apply for the new tenant.
 * - Next login / new tab: re-applied automatically (sessionStorage cleared).
 */

import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { isEqual } from '@/utils/lodash.util';

export function useDefaultStatusFilter<F>({
  sessionKey,
  applyFilter,
  clearFilter,
  setFilters,
  filters,
  isFilteredEmpty,
  dataStatus,
  isFetching,
  enabled,
}: {
  sessionKey: string;
  applyFilter: (prev: F) => F;
  clearFilter: (prev: F) => F;
  setFilters: Dispatch<SetStateAction<F>>;
  filters: F;
  isFilteredEmpty: boolean;
  dataStatus: 'pending' | 'error' | 'success';
  isFetching: boolean;
  enabled: boolean;
}) {
  // NEW refs — replace the old appliedDefaultRef entirely
  const lastAppliedSessionKey = useRef<string | null>(null);
  // Applied-filter provenance, tracked PER sessionKey so one hook instance
  // switching sessionKeys (e.g. Seeds tab changes) never overwrites another
  // key's pending auto-clear provenance.
  const appliedFiltersBySessionKey = useRef(new Map<string, F>());

  useLayoutEffect(() => {
    if (!enabled) return;
    if (lastAppliedSessionKey.current === sessionKey) return;

    let alreadyInitialized = false;
    try {
      alreadyInitialized = !!sessionStorage.getItem(sessionKey);
    } catch {
      // sessionStorage unavailable (Safari private)
    }
    if (alreadyInitialized) {
      lastAppliedSessionKey.current = sessionKey;
      return;
    }

    // Capture what WE wrote for later provenance compare.
    setFilters(prev => {
      const next = applyFilter(prev);
      appliedFiltersBySessionKey.current.set(sessionKey, next);
      return next;
    });
    lastAppliedSessionKey.current = sessionKey;

    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch {
      // sessionStorage unavailable
    }
  }, [enabled, setFilters, sessionKey, applyFilter]);

  useEffect(() => {
    if (!enabled) return;
    if (dataStatus !== 'success' || isFetching) return;
    if (!isFilteredEmpty) return;
    const applied = appliedFiltersBySessionKey.current.get(sessionKey);
    if (applied == null) return;
    // Provenance gate: if the user touched state, the entry no longer matches.
    if (!isEqual(filters, applied)) return;

    setFilters(clearFilter);
    appliedFiltersBySessionKey.current.delete(sessionKey);
  }, [
    enabled,
    dataStatus,
    isFetching,
    isFilteredEmpty,
    filters,
    setFilters,
    clearFilter,
    sessionKey,
  ]);
}
